import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { CreditCard, Lock, CheckCircle } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../utils/api';

interface PaymentCheckoutProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  paymentType: 'premium_listing' | 'booking_fee' | 'featured_promotion' | 'job_post' | 'subscription' | 'booking_priority';
  amount: number;
  title: string;
  description?: string;
  metadata?: Record<string, any>;
  providerId?: number;
  bookingId?: number;
  promotionId?: number;
  jobPostId?: number;
}

const PaymentCheckout: React.FC<PaymentCheckoutProps> = ({
  open,
  onClose,
  onSuccess,
  paymentType,
  amount,
  title,
  description,
  metadata = {},
  providerId,
  bookingId,
  promotionId,
  jobPostId
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>('monthly');
  const [duration, setDuration] = useState(1);

  const handlePayment = async () => {
    if (!user) {
      setError('Please log in to make a payment');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const paymentMetadata = {
        ...metadata,
        ...(paymentType === 'subscription' && { plan: selectedPlan }),
        ...(paymentType === 'premium_listing' && { duration })
      };

      const response = await fetch(`${API_BASE_URL}/payment/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: paymentType,
          amount,
          userId: user.id,
          providerId,
          bookingId,
          promotionId,
          jobPostId,
          metadata: paymentMetadata
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create payment');
      }

      const data = await response.json();

      // Redirect to YOCO checkout
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err: any) {
      setError(err.message || 'Payment initialization failed');
      setLoading(false);
    }
  };

  const getPlanAmount = () => {
    if (paymentType === 'subscription') {
      return selectedPlan === 'yearly' ? amount * 12 * 0.9 : amount; // 10% discount for yearly
    }
    return amount;
  };

  const getFinalAmount = () => {
    if (paymentType === 'premium_listing') {
      return amount * duration;
    }
    return getPlanAmount();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CreditCard color="primary" />
          <Typography variant="h6">{title}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {description}
          </Typography>
        )}

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="body1">Payment Type:</Typography>
              <Typography variant="body1" fontWeight="bold">
                {paymentType.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
              </Typography>
            </Box>

            {paymentType === 'subscription' && (
              <>
                <Divider sx={{ my: 2 }} />
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend">Subscription Plan</FormLabel>
                  <RadioGroup
                    value={selectedPlan}
                    onChange={(e) => setSelectedPlan(e.target.value as 'monthly' | 'yearly')}
                  >
                    <FormControlLabel
                      value="monthly"
                      control={<Radio />}
                      label={`Monthly - R${amount.toFixed(2)}/month`}
                    />
                    <FormControlLabel
                      value="yearly"
                      control={<Radio />}
                      label={`Yearly - R${(amount * 12 * 0.9).toFixed(2)}/year (Save 10%)`}
                    />
                  </RadioGroup>
                </FormControl>
              </>
            )}

            {paymentType === 'premium_listing' && (
              <>
                <Divider sx={{ my: 2 }} />
                <TextField
                  label="Duration (months)"
                  type="number"
                  fullWidth
                  value={duration}
                  onChange={(e) => setDuration(parseInt(e.target.value) || 1)}
                  inputProps={{ min: 1, max: 12 }}
                  sx={{ mb: 2 }}
                />
              </>
            )}

            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Total Amount:</Typography>
              <Typography variant="h5" color="primary" fontWeight="bold">
                R{getFinalAmount().toFixed(2)}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Lock color="primary" fontSize="small" />
          <Typography variant="body2" color="text.secondary">
            Secure payment powered by YOCO. Your payment information is encrypted and secure.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handlePayment}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <CreditCard />}
        >
          {loading ? 'Processing...' : `Pay R${getFinalAmount().toFixed(2)}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentCheckout;



